<!-- 
Panel of actions that can be performed against a provider, as well as ones that aren't currently available.
-->
<template>
  <div class="pb-3 mx-2">
    <v-fade-transition>
      <div>
        <v-text-field
          v-model="search"
          :label="loading ? 'Loading...' : 'Search provider actions'"
          outline
          prepend-inner-icon="search"
          clearable
          hide-details
          class="mb-2"
        />
        <p v-if="!showNotApplicable && noApplicableTasks && !loading" class="mt-4">
          None.
        </p>
        <v-hover v-for="task in applicableTasks" :key="task.type" class="my-1">
          <v-card
            slot-scope="{ hover }"
            :v-ripple="canRun(task)"
            :style="canRun(task) ? 'cursor:pointer' : 'cursor:default'"
            :class="canRun(task) ? '' : 'grey--text text--darken-1'"
            flat
            color="transparent"
            @click.stop="e => canRun(task) && run(task)"
            @keydown.enter="e => canRun(task) && run(task)"
          >
            <div v-if="hover && canRun(task)" class="d-flex reveal"/>
            <v-card-text class="pt-2 pb-1">
              <h3 class="subheading mb-2">
                <v-icon :color="canRun(task) ? 'primary' : ''" class="task-icon mr-2">{{ getIcon(task.type) }}</v-icon>
                {{ lookups.providerTaskTypeDescriptions[task.type] }}
                <v-progress-circular 
                  v-if="running(task)" 
                  size="22" class="ml-2" indeterminate color="default"
                />
                <v-tooltip v-if="!canRunTask(task.claimType)" bottom>
                  <v-icon slot="activator" color="red darken-4" class="subheading lock">lock</v-icon>
                  <span>You do not have permission to run this task</span>
                </v-tooltip>
              </h3>
              <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
              <p v-if="task.description" class="caption" v-html="task.description"/>
            </v-card-text>
          </v-card>
        </v-hover>
        <v-btn
          v-if="!search && !loading"
          flat small class="mt-2 ml-3"
          @click="showNotApplicable=!showNotApplicable"
        >
          Show unavailable actions ({{ inapplicableTasks.length }})
          <our-expand-icon :expanded="showNotApplicable"/>
        </v-btn>
        <div v-if="showNotApplicable || search" class="ml-4 mr-4">
          <div v-for="task in inapplicableTasks" :key="task.type">
            <div class="mt-4 mb-5">
              <h3 class="grey--text subheading mb-2">
                <v-icon color="grey" class="task-icon mr-2">{{ getIcon(task.type) }}</v-icon>
                {{ lookups.providerTaskTypeDescriptions[task.type] }}
              </h3>
              <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
              <p class="grey--text caption" v-html="task.description"/>
              <p class="grey--text caption">Not currently applicable because {{ task.notApplicableReason }}.</p>
            </div>
          </div>
        </div>
        <p v-if="search && applicableTasks.length === 0 && inapplicableTasks.length === 0" class="ma-4 grey--text text--darken-1">
          No provider actions found containing '{{ search }}'.
        </p>
      </div>
    </v-fade-transition>
    <component 
      :is="currentTaskComponent"
      v-model="showRunTaskDialog"
      :levy-collection-id="levyCollectionId" 
      :provider-id="providerId" 
      :task="taskToRun"
    />
  </div>
</template>

<script>
import taskMixins from "@/mixins/task.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import ProviderTaskDialog from "@/components/levy-collection-provider-tasks/ProviderTaskDialog.vue";
import ProviderTaskDialogForNotices from "@/components/levy-collection-provider-tasks/ProviderTaskDialogForNotices.vue";
import ProviderTaskDialogWithExplanation from "@/components/levy-collection-provider-tasks/ProviderTaskDialogWithExplanation.vue";
import ProviderTaskDialogForRaisingInvoice from "@/components/levy-collection-provider-tasks/ProviderTaskDialogForRaisingInvoice.vue";
import PaymentStatusDialog from "@/components/levy-collection-providers/PaymentStatusDialog.vue";
import FinaliseDialog from "@/components/levy-collection-providers/FinaliseDialog.vue";
import AdjustLevyCalculationsDialog from "@/components/levy-collection-overrides/AdjustLevyCalculationsDialog.vue";
import RemoveProviderDialog from "@/components/levy-collection-overrides/RemoveProviderDialog.vue";
import RemovePreviouslyAddedProviderDialog from "@/components/levy-collection-overrides/RemovePreviouslyAddedProviderDialog.vue";
import ChangeInvoiceAddresseeDialog from "@/components/levy-collection-overrides/ChangeInvoiceAddresseeDialog.vue";

export default {
  components: {
    ProviderTaskDialog,
    ProviderTaskDialogForNotices,
    ProviderTaskDialogWithExplanation,
    ProviderTaskDialogForRaisingInvoice,
    PaymentStatusDialog,
    FinaliseDialog,
    AdjustLevyCalculationsDialog,
    RemoveProviderDialog,
    RemovePreviouslyAddedProviderDialog,
    ChangeInvoiceAddresseeDialog
  },
  mixins: [lookupMixins, taskMixins],
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    },
    providerId: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      search: "",
      showNotApplicable: false,
      showRunTaskDialog: false,
      taskToRun: {
        type: null,
        uiComponent: null,
        description: null
      }
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionProviders.tasks;
    },
    loaded() {
      return this.state.loaded;
    },
    tasks() {
      return this.state.data;
    },
    loading() {
      return this.state.loading;
    },
    processing() {
      return this.$store.state.levyCollectionProviders.task.processing;
    },
    currentTaskComponent() {
      return this.taskToRun.uiComponent ? this.taskToRun.uiComponent : "ProviderTaskDialog";
    },
    applicableTasks() {
      return this.searchTasks(
        this.tasks.filter(t => t.isApplicable),
        this.lookups.providerTaskTypeDescriptions,
        this.search
      );
    },
    inapplicableTasks() {
      return this.searchTasks(
        this.tasks.filter(
          t =>
            !t.isApplicable &&
            !this.doesLookupHaveAttribute(t.type, "DontShowIfInapplicable", this.lookups.providerTaskTypes)
        ),
        this.lookups.providerTaskTypeDescriptions,
        this.search
      );
    },
    noApplicableTasks() {
      return this.tasks.every(t => !t.isApplicable);
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    providerId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch(lcpAction.TASKS_REFRESH, {
        levyCollectionId: this.levyCollectionId,
        providerId: this.providerId
      });
    },
    run(task) {
      this.taskToRun = task;
      if (task.uiComponent) {
        // required to ensure that the watchers for "show" on our task dialogs fire
        this.$nextTick(() => (this.showRunTaskDialog = true));
      } else {
        this.$store.dispatch(lcpAction.TASK_RUN, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId,
          type: task.type
        });
      }
    },
    canRun(task) {
      return !this.loading && !this.processing && this.canRunTask(task.claimType);
    },
    running(task) {
      return task == this.taskToRun && this.processing;
    }
  }
};
</script>

<style lang="scss" scoped>
.title .lock {
  line-height: 22px;
}
.task-icon {
  position: relative;
  top: 2px;
}
</style>